import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { ChevronLeft, ChevronRight, Video, FileVideo } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ScheduleCallModal } from "./ScheduleCallModal";
import { RequestRecordingModal } from "./RequestRecordingModal";
import { StartLiveCallModal } from "./StartLiveCallModal";
import { useToast } from "@/shared/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/apiClient";
import { useNavigate } from "react-router-dom";
import { useExtensionHcpPrefill } from "@/shared/hooks/useExtensionHcpPrefill";
interface MonthYearPickerProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  monthNames: string[];
}

function MonthYearPicker({ currentDate, onDateChange, monthNames }: MonthYearPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());

  // Reset selected month/year when picker opens to match current date
  useEffect(() => {
    if (isOpen) {
      setSelectedYear(currentDate.getFullYear());
      setSelectedMonth(currentDate.getMonth());
    }
  }, [isOpen, currentDate]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

  // Scroll to current year when year selector opens
  useEffect(() => {
    if (showYearSelector) {
      setTimeout(() => {
        const yearContainer = document.querySelector('.year-selector-container');
        const currentYearButton = document.querySelector(`[data-year="${currentYear}"]`);
        if (yearContainer && currentYearButton) {
          const containerRect = yearContainer.getBoundingClientRect();
          const buttonRect = currentYearButton.getBoundingClientRect();
          const scrollTop = buttonRect.top - containerRect.top - (containerRect.height / 2) + (buttonRect.height / 2);
          yearContainer.scrollTop = scrollTop;
        }
      }, 100);
    }
  }, [showYearSelector, currentYear]);

  const handleApply = () => {
    onDateChange(new Date(selectedYear, selectedMonth, 1));
    setIsOpen(false);
    setShowYearSelector(false);
  };

  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setShowYearSelector(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) {
        setShowYearSelector(false);
      }
    }}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          className="text-2xl font-bold hover:bg-accent/50 p-2 h-auto"
        >
          {monthNames[currentDate.getMonth()]}, {currentDate.getFullYear()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4">
          {!showYearSelector ? (
            <>
              {/* Header with navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMonthChange('prev')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-center">
                  <div className="text-lg font-semibold">
                    {monthNames[selectedMonth]} 
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowYearSelector(true)}
                      className="ml-2"
                    >
                      {selectedYear}
                    </Button>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMonthChange('next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Month Selection */}
              <div className="mb-4">
                <div className="text-sm font-medium mb-2 text-center">Select Month</div>
                <div className="grid grid-cols-3 gap-2">
                  {monthNames.map((month, index) => (
                    <Button
                      key={month}
                      variant={index === selectedMonth ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedMonth(index)}
                      className="text-xs"
                    >
                      {month.slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleApply}>
                  Apply
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Year Selection */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowYearSelector(false)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="text-lg font-semibold">Select Year</div>
                  <div className="w-8"></div>
                </div>
                <div className="max-h-48 overflow-y-auto border rounded year-selector-container">
                  {years.map((year) => (
                    <Button
                      key={year}
                      data-year={year}
                      variant={year === selectedYear ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleYearSelect(year)}
                      className={`w-full justify-center text-sm ${year === currentYear ? 'font-bold' : ''}`}
                    >
                      {year}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


interface CalendarEvent {
  id: string;
  title: string;
  date: number;
  type: "meeting" | "design" | "visit" | "misc" | "scheduled-call";
  start_time?: string;
  end_time?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  project?: string;
  assigned_to?: string;
  room_id?: string;
}

interface WeekEvent {
  title: string;
  color: string;
  startOffset: number;
  duration: number;
}

export function Calendar() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const extensionHcpPrefill = useExtensionHcpPrefill();
  const [currentDate, setCurrentDate] = useState(new Date()); // Set to current date
  const [viewMode, setViewMode] = useState<"Day" | "Week" | "Month">("Week");
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isRequestRecordingModalOpen, setIsRequestRecordingModalOpen] = useState(false);
  const [isStartLiveCallModalOpen, setIsStartLiveCallModalOpen] = useState(false);
  const [isOptionsDialogOpen, setIsOptionsDialogOpen] = useState(false);
  const [isPastDayModalOpen, setIsPastDayModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [pastDayCalls, setPastDayCalls] = useState<CalendarEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);

  // Keep userId in sync with auth so we refetch when user becomes available
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id ?? null));
    return () => subscription.unsubscribe();
  }, []);

  // Load calendar events when user is available, date view changes, or manual refresh
  useEffect(() => {
    if (!userId) return;

    const loadCalendarEvents = async () => {
      try {
        // SECURITY: Use backend API instead of direct database access
        const response = await apiClient.get<{
          ok: boolean;
          events: any[];
          todayEvents: any[];
          upcomingEvents: any[];
        }>('/calendar/events');

        if (!response.ok) {
          console.error('Error loading calendar events');
          return;
        }

        const data = response.events;
        console.log('Loaded calendar events:', data);

        // Transform database events to calendar format
        const transformedEvents: CalendarEvent[] = (data ?? []).map(event => ({
          id: event.id,
          title: event.title,
          date: new Date(event.start_time).getDate(),
          type: "scheduled-call",
          start_time: event.start_time,
          end_time: event.end_time,
          customer_name: event.customer_name,
          customer_phone: event.customer_phone,
          customer_email: event.customer_email,
          project: event.project,
          assigned_to: event.assigned_to,
          room_id: event.room_id
        }));

        console.log('Transformed events:', transformedEvents);
        setCalendarEvents(transformedEvents);
      } catch (error) {
        console.error('Outer error fetching calendar events:', error);
      }
    };

    loadCalendarEvents();
  }, [userId, currentDate, refreshKey]);

  // Refetch when window regains focus (helps if first load ran before auth was ready)
  useEffect(() => {
    const onFocus = () => {
      if (userId) setRefreshKey((k) => k + 1);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [userId]);

  // Realtime updates for calendar_events
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setup = async () => {
      try {
        // CRITICAL FIX: Use localStorage instead of getUser() to prevent hanging
        const { getUserId } = await import('@/shared/utils/supabaseStorage');
        const userId = getUserId();
        if (!userId) return;

        channel = supabase
          .channel('calendar-events-realtime')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'calendar_events' }, (payload: any) => {
            const row = payload?.new ?? payload?.old;
            if (!row || row.user_id === userId) {
              setRefreshKey((k) => k + 1);
            }
          })
          .subscribe();
      } catch (error) {
        console.error('[Calendar] Error setting up realtime subscription:', error);
      }
    };

    setup();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  // Listen for manual calendar refresh events
  useEffect(() => {
    const handler = () => setRefreshKey((k) => k + 1);
    window.addEventListener('calendar:refresh', handler);
    return () => window.removeEventListener('calendar:refresh', handler);
  }, []);


  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Convert Sunday (0) to 6, make Monday 0
  };

  const getEventForDate = (day: number) => {
    // Filter events for the current month and day
    const currentMonthEvents = calendarEvents.filter(event => {
      if (!event.start_time) return false;
      const eventDate = new Date(event.start_time);
      return eventDate.getMonth() === currentDate.getMonth() &&
             eventDate.getFullYear() === currentDate.getFullYear() &&
             eventDate.getDate() === day;
    });
    return currentMonthEvents[0]; // Return the first event for that day
  };

  const getAllEventsForDate = (day: number) => {
    // Filter all events for the current month and day
    const currentMonthEvents = calendarEvents.filter(event => {
      if (!event.start_time) return false;
      const eventDate = new Date(event.start_time);
      return eventDate.getMonth() === currentDate.getMonth() &&
             eventDate.getFullYear() === currentDate.getFullYear() &&
             eventDate.getDate() === day;
    });
    return currentMonthEvents;
  };

  const getEventsForExactDate = (date: Date) => {
    return calendarEvents.filter(event => {
      if (!event.start_time) return false;
      const eventDate = new Date(event.start_time);
      return eventDate.getFullYear() === date.getFullYear() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getDate() === date.getDate();
    });
  };

  const getEventsForHourSlot = (date: Date, hour: number) => {
    return getEventsForExactDate(date).filter(event => {
      if (!event.start_time) return false;
      const eventDate = new Date(event.start_time);
      return eventDate.getHours() === hour;
    });
  };

  const getCurrentDay = () => {
    const today = new Date();
    return today.getDate();
  };

  const isCurrentMonth = () => {
    const today = new Date();
    return currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  const getEventColor = (type: string, isPastEvent: boolean = false, event?: CalendarEvent) => {
    // Check if scheduled call is in the past
    const isScheduledCallPast = event && event.start_time 
      ? new Date().getTime() > new Date(event.start_time).getTime()
      : false;

    switch (type) {
      case "meeting":
        return isPastEvent ? "bg-green-100 text-green-700 border border-green-200" : "bg-primary text-primary-foreground";
      case "scheduled-call":
        return isScheduledCallPast ? "bg-green-200 text-green-800 border border-green-300" : "bg-green-500 text-white border border-green-600";
      case "design":
        return isPastEvent ? "bg-green-100 text-green-700 border border-green-200" : "bg-secondary text-secondary-foreground";
      case "visit":
        return isPastEvent ? "bg-green-100 text-green-700 border border-green-200" : "bg-accent text-accent-foreground";
      default:
        return isPastEvent ? "bg-green-100 text-green-700 border border-green-200" : "bg-muted text-muted-foreground";
    }
  };

  const renderDayView = () => {
    const dayToDisplay = selectedDate ?? currentDate;
    const today = new Date();
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayEvents = getEventsForExactDate(dayToDisplay);
    
    return (
      <div className="flex justify-center w-full bg-muted/30 min-h-[400px]">
        <div className="border-t border-border w-1/2 bg-background">
          <div className="grid grid-cols-1 gap-0">
            {/* Header for the day */}
            <div className="p-2 border-b border-border bg-muted/30 text-center">
              <h3 className="text-lg font-semibold">
                {dayToDisplay.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                {dayEvents.length > 0 && (
                  <span className="block text-sm text-muted-foreground mt-1">
                    {dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}
                  </span>
                )}
              </h3>
            </div>
            
            {/* Hour slots */}
            <div className="max-h-[400px] overflow-y-auto">
              {hours.map((hour) => {
                const slotEvents = getEventsForHourSlot(dayToDisplay, hour);
                const isPastSlot = new Date(dayToDisplay).setHours(hour, 59, 59, 999) < today.getTime();
                const timeString = hour === 0 ? '12:00 AM' : 
                                hour < 12 ? `${hour}:00 AM` : 
                                hour === 12 ? '12:00 PM' : 
                                `${hour - 12}:00 PM`;
                
                return (
                  <div key={hour} className="flex border-b border-border hover:bg-muted/30 transition-colors">
                    <div className="w-16 p-2 text-xs font-medium text-muted-foreground border-r border-border">
                      {timeString}
                    </div>
                    <div className="flex-1 p-2 min-h-[40px] cursor-pointer hover:bg-accent/50" 
                         onClick={() => handleTimeSlotClick(dayToDisplay, hour, isPastSlot)}>
                      {slotEvents.map((event) => {
                        const eventDate = event.start_time ? new Date(event.start_time) : null;
                        const eventLabel = eventDate 
                          ? eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : 'Scheduled';
                        const isPastEvent = !!eventDate && eventDate < new Date();
                        return (
                          <div
                            key={event.id}
                            className={`mb-1 p-2 rounded text-xs ${getEventColor(event.type, isPastEvent, event)} shadow-sm`}
                          >
                            <div className="font-semibold">{event.title}</div>
                            <div className="text-[10px] text-muted-foreground flex justify-between">
                              <span>{eventLabel}</span>
                              {event.customer_name && <span>{event.customer_name}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCalendarGrid = () => {
    if (viewMode === "Day") {
      return renderDayView();
    }
    
    if (viewMode === "Week") {
      return renderWeekView();
    }
    
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = getCurrentDay();
    const isCurrentMonthView = isCurrentMonth();

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={`aspect-square p-1 border-b border-border ${i < 6 ? 'border-r' : ''}`}></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isToday = isCurrentMonthView && day === today;
      const events = getAllEventsForDate(day);
      
      days.push(
        <button
          key={day}
          onClick={() => handleDayClick(dayDate, dayDate < new Date() && !isToday)}
          className={`aspect-square p-1 border-b border-border relative hover:bg-accent/50 transition-colors ${isToday ? 'bg-primary/10' : ''} ${(firstDay + day - 1) % 7 < 6 ? 'border-r' : ''}`}
        >
          <div className="h-full flex flex-col">
            <span className={`text-sm font-medium mb-1 ${isToday ? 'text-primary font-bold bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs' : 'text-foreground'}`}>
              {day}
            </span>
            {events.slice(0, 3).map((evt) => {
              const isPastEvent = evt.start_time ? new Date(evt.start_time) < new Date() : false;
              return (
                <div key={evt.id} className={`text-xs p-1 rounded ${getEventColor(evt.type, isPastEvent, evt)} truncate mb-1`}>
                  {evt.title}
                </div>
              );
            })}
            {events.length > 3 && (
              <div className="text-[10px] text-muted-foreground">+{events.length - 3} more</div>
            )}
          </div>
        </button>
      );
    }

    return days;
  };

  const renderWeekView = () => {
    const today = new Date();
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday
    startOfWeek.setDate(diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      const dayNum = currentDay.getDate();
      const isToday = currentDay.toDateString() === today.toDateString();
      
      // Check for events on this day
      const dayEvents = calendarEvents.filter(evt => {
        if (!evt.start_time) return false;
        const d = new Date(evt.start_time);
        return d.getDate() === currentDay.getDate() &&
               d.getMonth() === currentDay.getMonth() &&
               d.getFullYear() === currentDay.getFullYear();
      });
      
      weekDays.push(
        <button
          key={i}
          onClick={() => handleDayClick(currentDay, currentDay < today && !isToday)}
          className={`aspect-square p-1 border-b border-border relative hover:bg-accent/50 transition-colors ${isToday ? 'bg-primary/10' : ''} ${i < 6 ? 'border-r' : ''}`}
        >
          <div className="h-full flex flex-col">
            <span className={`text-sm font-medium mb-1 ${isToday ? 'text-primary font-bold bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs' : 'text-foreground'}`}>
              {dayNum}
            </span>
            {dayEvents.slice(0, 3).map((evt) => {
              const isPastEvent = evt.start_time ? new Date(evt.start_time) < new Date() : false;
              return (
                <div key={evt.id} className={`text-xs p-1 rounded ${getEventColor(evt.type, isPastEvent, evt)} truncate mb-1`}>
                  {evt.title}
                </div>
              );
            })}
            {dayEvents.length > 3 && (
              <div className="text-[10px] text-muted-foreground">+{dayEvents.length - 3} more</div>
            )}
          </div>
        </button>
      );
    }
    
    return weekDays;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);

      // Navigate by day when in Day view, by week when in Week view, by month in Month view
      if (viewMode === 'Day') {
        const daysToAdd = direction === 'prev' ? -1 : 1;
        newDate.setDate(newDate.getDate() + daysToAdd);
      } else if (viewMode === 'Week') {
        const daysToAdd = direction === 'prev' ? -7 : 7;
        newDate.setDate(newDate.getDate() + daysToAdd);
      } else {
        // Month view
        if (direction === 'prev') {
          newDate.setMonth(newDate.getMonth() - 1);
        } else {
          newDate.setMonth(newDate.getMonth() + 1);
        }
      }

      return newDate;
    });
  };

  const handleDayClick = (day: Date, isPast: boolean) => {
    setSelectedDate(day);
    
    // Get all events for this day
    const dayEvents = calendarEvents.filter(event => {
      if (!event.start_time) return false;
      const eventDate = new Date(event.start_time);
      return eventDate.getDate() === day.getDate() &&
             eventDate.getMonth() === day.getMonth() &&
             eventDate.getFullYear() === day.getFullYear();
    });

    if (dayEvents.length > 0) {
      // Show events for this day regardless of past/future
      setPastDayCalls(dayEvents);
      setIsPastDayModalOpen(true);
    } else if (!isPast) {
      // Show options for present/future days with no events
      setIsOptionsDialogOpen(true);
    }
  };

  const handleScheduleCall = () => {
    setIsOptionsDialogOpen(false);
    setIsScheduleModalOpen(true);
  };

  const handleRequestRecording = () => {
    setIsOptionsDialogOpen(false);
    setIsRequestRecordingModalOpen(true);
  };

  const handleStartLiveCall = () => {
    setIsOptionsDialogOpen(false);
    setIsStartLiveCallModalOpen(true);
  };

  const handleStartVideoCall = async (call: CalendarEvent) => {
    console.log('[Calendar] handleStartVideoCall called with:', {
      callId: call.id,
      roomId: call.room_id,
      customerName: call.customer_name,
      startTime: call.start_time
    });

    const now = new Date();
    const callTime = call.start_time ? new Date(call.start_time) : now;
    const isPastCall = callTime < now;

    try {
      if (isPastCall) {
        // Navigate to session summary for past calls with basic call context
        navigate('/session-summary', {
          state: {
            callData: {
              roomId: call.id,
              durationMinutes: 0,
              isHost: true,
              customerData: {
                name: call.customer_name,
                email: call.customer_email,
                phone: call.customer_phone,
                project: call.project,
              },
            },
          },
        });
        return;
      }

      // MUST use existing room_id from calendar event - never generate new one for scheduled calls
      if (!call.room_id) {
        console.error('[Calendar] ERROR: No room_id found for scheduled call:', call.id);
        toast({
          title: "Error",
          description: "This scheduled call is missing room information. Please reschedule.",
          variant: "destructive"
        });
        return;
      }

      console.log('[Calendar] Starting scheduled call:', {
        eventId: call.id,
        roomId: call.room_id,
        customerName: call.customer_name
      });

      // CRITICAL: Navigate to prejoin lobby with host=1 parameter
      // This ensures the host goes through prejoin to set name/test devices
      navigate(`/join/${call.room_id}?host=1&eventId=${call.id}`);
    } catch (error) {
      console.error('Error starting scheduled video call:', error);
      toast({
        title: "Error",
        description: "Failed to start video call. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCancelEvent = async (eventId: string) => {
    try {
      // SECURITY: Get calendar event details via backend API before deletion
      const eventResponse = await apiClient.get<{ ok: boolean; event: any }>(
        `/calendar/events/${eventId}`
      );

      if (!eventResponse.ok || !eventResponse.event) {
        throw new Error('Failed to fetch calendar event');
      }

      const calendarEvent = eventResponse.event;

      // Get linked sessions via backend API
      const sessionsResponse = await apiClient.get<{ ok: boolean; sessions: any[] }>(
        `/calendar/events/${eventId}/sessions`
      );

      const linkedSessions = sessionsResponse.ok ? sessionsResponse.sessions : [];

      if (linkedSessions && linkedSessions.length > 0) {
        // If there's a linked call_session, nullify the foreign key references first
        // SECURITY: Use backend API instead of direct Supabase call
        await apiClient.post('/calls/unlink-calendar-event', { eventId });
      }

      // SECURITY: Delete the calendar event via backend API
      await apiClient.delete(`/calendar/events/${eventId}`);

      // Send cancellation notifications (email and SMS)
      if (calendarEvent?.customer_email || calendarEvent?.customer_phone) {
        try {
          // SECURITY: Get user profile via backend API
          const profileResponse = await apiClient.get<{ ok: boolean; profile: any }>(
            '/user/profile'
          );

          const hostName = profileResponse.ok && profileResponse.profile?.full_name
            ? profileResponse.profile.full_name
            : 'Your host';
          const customerName = calendarEvent.customer_name || 'Customer';
          const scheduledTime = calendarEvent.start_time
            ? new Date(calendarEvent.start_time).toLocaleString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })
            : 'N/A';

          // Send email notification if customer has email
          if (calendarEvent.customer_email) {
            try {
              // SECURITY: Use backend proxy instead of calling edge function directly
              await apiClient.post('/edge/send-email', {
                to: calendarEvent.customer_email,
                subject: `Call Cancelled - ${hostName}`,
                html: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <h2 style="color: #dc2626;">Call Cancelled</h2>
                    <p>Hello ${customerName},</p>
                    <p>Your scheduled video call with <strong>${hostName}</strong> has been cancelled.</p>
                    <div style="background-color: #fee2e2; border-left: 4px solid #dc2626; padding: 12px; margin: 20px 0;">
                      <p style="margin: 0; color: #991b1b;"><strong>Cancelled Call Details:</strong></p>
                      <p style="margin: 8px 0 0 0; color: #991b1b;">Originally scheduled for: ${scheduledTime}</p>
                    </div>
                    <p>If you have any questions or would like to reschedule, please contact ${hostName} directly.</p>
                    <p style="color: #666; font-size: 12px; margin-top: 30px;">This notification was sent by ${hostName} via Wlkthru.</p>
                  </div>
                `,
                from: 'noreply@wlkthru.co',
                fromName: 'Wlkthru',
              });
              console.log(`✅ Cancellation email sent to ${calendarEvent.customer_email}`);
            } catch (emailError) {
              console.error('⚠️ Failed to send cancellation email:', emailError);
            }
          }

          // Send SMS notification if customer has phone
          if (calendarEvent.customer_phone) {
            try {
              const smsMessage = `CALL CANCELLED - Your scheduled video call with ${hostName} on ${scheduledTime} has been cancelled. If you have any questions, please contact ${hostName} directly.`;

              // SECURITY: Use backend proxy instead of calling edge function directly
              const smsData = await apiClient.post<{ success: boolean }>('/edge/send-sms', {
                to: calendarEvent.customer_phone,
                message: smsMessage,
                type: 'call-cancelled',
              });

              if (!smsData?.success) {
                console.error('SMS sending failed:', smsData);
              } else {
                console.log(`✅ Cancellation SMS sent to ${calendarEvent.customer_phone}`);
              }
            } catch (smsError) {
              console.error('⚠️ Failed to send cancellation SMS:', smsError);
            }
          }
        } catch (error) {
          console.error('⚠️ Failed to send cancellation notifications:', error);
          // Don't fail the cancellation if notifications fail
        }
      }

      toast({
        title: "Event Canceled",
        description: "The scheduled call has been canceled successfully."
      });

      // Close all modals and refresh calendar
      setIsPastDayModalOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (error: any) {
      console.error('Error canceling event:', error);
      toast({
        title: "Error",
        description: "Failed to cancel the event. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleTimeSlotClick = (day: Date, timeIndex: number, isPast: boolean) => {
    if (isPast) {
      // Show past calls for this time slot
      console.log('Show past calls for:', day.toDateString(), 'at hour:', timeIndex);
    } else {
      // Open schedule video call modal for specific time
      console.log('Schedule call for:', day.toDateString(), 'at hour:', timeIndex);
      // TODO: Implement scheduling functionality with specific time
    }
  };

  const getSampleEvents = (day: Date, timeIndex: number): WeekEvent[] => {
    // Sample events for demonstration - replace with actual data
    const events: WeekEvent[] = [];
    
    // Add some sample events based on day and time
    if (day.getDay() === 1 && timeIndex === 9) { // Monday 9 AM
      events.push({
        title: 'Team Meeting',
        color: 'bg-blue-500',
        startOffset: 0,
        duration: 50
      });
    }
    
    if (day.getDay() === 3 && timeIndex === 14) { // Wednesday 2 PM
      events.push({
        title: 'Client Call',
        color: 'bg-green-500',
        startOffset: 0,
        duration: 75
      });
    }
    
    if (day.getDay() === 5 && timeIndex === 10) { // Friday 10 AM
      events.push({
        title: 'Design Review',
        color: 'bg-purple-500',
        startOffset: 0,
        duration: 100
      });
    }
    
    return events;
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <MonthYearPicker 
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          monthNames={monthNames}
        />
        
        <div className="flex items-center space-x-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(["Day", "Week", "Month"] as const).map((mode) => (
              <Button
                key={mode}
                variant={viewMode === mode ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode(mode)}
                className="rounded-none border-r border-border last:border-r-0"
              >
                {mode}
              </Button>
            ))}
          </div>
          
          <div className="flex items-center space-x-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateMonth('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateMonth('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {viewMode === "Day" ? (
          renderCalendarGrid()
        ) : (
          <div className="grid grid-cols-7 border border-border">
            {/* Header row with day names */}
            {daysOfWeek.map((day, index) => (
              <div key={day} className={`p-3 text-center font-medium text-muted-foreground border-b border-border bg-muted/30 ${index < 6 ? 'border-r' : ''}`}>
                {day}
              </div>
            ))}
            
            {/* Calendar grid */}
            {renderCalendarGrid()}
          </div>
        )}
      </CardContent>

      {/* Options Dialog */}
      <Dialog open={isOptionsDialogOpen} onOpenChange={setIsOptionsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {selectedDate?.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col space-y-3 mt-4">
            <Button 
              onClick={handleStartLiveCall}
              variant="default"
              className="w-full justify-start"
              size="lg"
            >
              <Video className="h-5 w-5 mr-3" />
              Start Live Call
            </Button>
            <Button 
              onClick={handleScheduleCall}
              variant="secondary"
              className="w-full justify-start"
              size="lg"
            >
              <Video className="h-5 w-5 mr-3" />
              Schedule a Video Call
            </Button>
            <Button 
              onClick={handleRequestRecording}
              variant="outline"
              className="w-full justify-start hover:bg-accent hover:text-accent-foreground"
              size="lg"
            >
              <FileVideo className="h-5 w-5 mr-3" />
              Request a Recording
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ScheduleCallModal 
        open={isScheduleModalOpen} 
        onOpenChange={setIsScheduleModalOpen}
        prefillData={extensionHcpPrefill}
      />
      
      <RequestRecordingModal 
        open={isRequestRecordingModalOpen} 
        onOpenChange={setIsRequestRecordingModalOpen}
        prefillData={extensionHcpPrefill}
      />

      {/* Past Day Modal */}
      <Dialog open={isPastDayModalOpen} onOpenChange={setIsPastDayModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {selectedDate?.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {pastDayCalls.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">Calls on this day:</h4>
                {pastDayCalls.map((call) => {
                  const isFutureCall = call.start_time && new Date(call.start_time) > new Date();
                  return (
                    <div key={call.id}>
                      <div
                        className={`flex items-start space-x-3 p-3 border rounded-lg transition-colors ${
                          call.type === 'scheduled-call'
                            ? 'hover:bg-secondary/10 hover:border-secondary/30'
                            : ''
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full mt-1 ${getEventColor(call.type, false, call).split(' ')[0]}`}></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{call.title}</p>
                          {call.customer_name && (
                            <p className="text-xs text-muted-foreground">with {call.customer_name}</p>
                          )}
                          {call.start_time && (
                            <p className="text-xs text-muted-foreground">
                              {new Date(call.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                          <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${getEventColor(call.type, false, call)}`}>
                            {call.type.charAt(0).toUpperCase() + call.type.slice(1)}
                          </span>
                        </div>
                        {call.type === 'scheduled-call' && isFutureCall && (
                          <div className="flex gap-2">
                            <Button
                              variant="default"
                              size="sm"
                              className="h-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartVideoCall(call);
                              }}
                            >
                              Join Call
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEvent(call.id);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Add Schedule Call button for days with existing events */}
                <div className="pt-3 border-t">
                  <Button 
                    onClick={() => {
                      setIsPastDayModalOpen(false);
                      setIsScheduleModalOpen(true);
                    }}
                    variant="secondary"
                    className="w-full justify-start"
                    size="lg"
                  >
                    <Video className="h-5 w-5 mr-3" />
                    Schedule a Video Call
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground mb-4">No calls found on this day</p>
                <Button 
                  onClick={() => {
                    setIsPastDayModalOpen(false);
                    setIsScheduleModalOpen(true);
                  }}
                  variant="secondary"
                  className="w-full justify-start"
                  size="lg"
                >
                  <Video className="h-5 w-5 mr-3" />
                  Schedule a Video Call
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Start Live Call Modal */}
      <StartLiveCallModal
        open={isStartLiveCallModalOpen}
        onOpenChange={setIsStartLiveCallModalOpen}
        prefillData={extensionHcpPrefill}
      />

    </Card>
  );
}
